import React from 'react';
import { Badge, EmptyState, ErrorState, Alert } from '@parsina/framework';
import { ListChecks, Lock } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT, useTranslation } from '../../../i18n/I18nProvider.jsx';
import { listSeparator } from '../../../i18n/format.js';
import Panel from '../../../components/common/Panel.jsx';
import { formatMoney } from '../../../lib/money.js';
import { useTreatmentPlans } from '../../../hooks/useVisit.js';
import {
  isPlanLocked, labelFor, linePrice, sessionsOf, recordTitle,
  planCurrencyOf, outstandingByCurrency, planErrorMessage,
} from '../../Visit/Visit.shared.js';

// طرح درمان in the PATIENT's record, not only inside a visit.
//
// The plan was reachable from one place: the open visit. A dentist asking «what
// did we agree with this patient, and what is left?» between appointments — at
// reception, on the phone, before a recall — had nowhere to look, and a patient
// asking the same question got an answer nobody could see.
//
// ── Read-only, deliberately ─────────────────────────────────────────────────
// Adding a line, signing a plan and recording a treatment all happen IN a visit,
// because each of them is a clinical act that belongs to one: a line is quoted
// while the patient is in the chair, and a signature is taken there. This screen
// answers what was agreed and what is outstanding. It does not become a second
// place to quote from, which would be a second, unsigned quote disagreeing with
// the one the patient put their name to.
//
// ── Every course, newest first ──────────────────────────────────────────────
// Unlike the visit section — which shows the one OPEN plan — this shows them
// all. A finished course is what the patient's history is made of, and an
// abandoned one is part of the answer to «why did we not do that?».
//
// ── Each course in its own currency ─────────────────────────────────────────
// With no clinic chosen, the list spans every clinic this dentist works at, and
// two clinics may keep their books in different currencies. Every figure is
// therefore read in ITS plan's currency, and «مانده» is one total PER currency
// (outstandingByCurrency) — never a toman figure and a dirham figure added into
// a number that means nothing. There is no exchange rate anywhere in Parsina.

const STATUS_TONE = {
  proposed: { bg: CT.warnSoft, ink: CT.warnInk },
  accepted: { bg: CT.accentSoft, ink: CT.accentInk },
  done: { bg: CT.goodSoft, ink: CT.goodInk },
  rejected: { bg: CT.surface2, ink: C.textSecondary },
  deferred: { bg: CT.surface2, ink: C.textSecondary },
};

const PlanTab = ({ patientId, clinicId, t, formatNumber, formatDate }) => {
  // The same hook the visit uses, so the two surfaces cannot disagree about
  // what a plan IS, what its lines come to, or how many sessions are done.
  const {
    plans, catalog, records, loading, error, reload,
  } = useTreatmentPlans({ patientId, clinicId });

  const { locale } = useTranslation();
  const money = (amount, currency) => formatMoney(amount, currency, { t, locale });

  if (loading) {
    return (
      <Panel title={t('patient.tabs.plan')}>
        <div style={{ fontSize: 13, color: C.textMuted }}>{t('common.loading')}</div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel title={t('patient.tabs.plan')}>
        <div role="alert">
          <ErrorState
            title={t('visit.plan.error.title')}
            description={planErrorMessage(t, error) || t('visit.plan.error.body')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </div>
      </Panel>
    );
  }

  // Work, newest first — including what nobody ever quoted. A patient's
  // treatment history is not a subset of their plans: an emergency extraction
  // has a record and no plan line at all, so a screen built only from plans
  // would say it never happened.
  const done = (records || [])
    .slice()
    .sort((a, b) => (Number(b?.recordedAtMs) || 0) - (Number(a?.recordedAtMs) || 0));

  if (!plans.length && !done.length) {
    return (
      <Panel title={t('patient.tabs.plan')}>
        <EmptyState
          icon={<ListChecks size={40} color={C.textMuted} />}
          title={t('patient.plan.empty.title')}
          description={t('patient.plan.empty.description')}
        />
      </Panel>
    );
  }

  // Lines still waiting to happen, across every course — one total per currency.
  const outstanding = outstandingByCurrency(plans, catalog);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="patient-plan">
      {/* What is still owed across every open course — the one number somebody
          on the phone actually wants. One figure per currency, side by side
          and never added: a patient with courses at a toman clinic and a
          dirham one owes two amounts, not their sum. */}
      {outstanding.count > 0 && (
        <Alert variant="info" data-testid="patient-plan-outstanding">
          {t('patient.plan.outstanding', {
            count: formatNumber(outstanding.count),
            amount: outstanding.totals
              .map(({ amount, currency }) => money(amount, currency))
              .join(listSeparator()),
          })}
        </Alert>
      )}

      {/* «درمان‌های انجام‌شده» — the answer to «what has actually been done for
          this patient», which no plan can give. It is FIRST because it is the
          question somebody on the phone or at the chair asks first; the courses
          below say what is still owed. */}
      {done.length > 0 && (
        <Panel title={t('patient.plan.doneTitle')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="patient-records">
            {done.map((r) => {
              const struck = !!r.voidedAtMs;
              return (
                <div
                  key={r.id}
                  data-state={struck ? 'voided' : 'live'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    border: `1px solid ${C.border}`, borderRadius: 10,
                    padding: '8px 11px',
                    background: struck ? CT.surface2 : CT.surface,
                    opacity: struck ? 0.75 : 1,
                  }}
                >
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: C.textSecondary,
                    minWidth: 34, textAlign: 'center', flexShrink: 0,
                  }}>
                    {r.toothFdi
                      ? <span {...LTR_TEXT}>{formatNumber(r.toothFdi)}</span>
                      : t('visit.plan.wholeMouthShort')}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 12.5, fontWeight: 700, color: C.textPrimary,
                      textDecoration: struck ? 'line-through' : 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {recordTitle(r, catalog) || t('visit.plan.untitledItem')}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>
                      {[
                        r.recordedAtMs && formatDate
                          ? formatDate(new Date(Number(r.recordedAtMs)))
                          : null,
                        r.surfaces || null,
                        r.material ? t(`tooth.material.${r.material}`) : null,
                        struck
                          ? t('visit.record.voidedFor', { reason: r.voidReason || '—' })
                          : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {plans.map((plan) => {
        const locked = isPlanLocked(plan);
        const currency = planCurrencyOf(plan);
        const items = plan.items || [];
        return (
          <Panel
            key={plan.id}
            title={plan.title || t('visit.plan.title')}
            action={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {locked && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11.5, color: C.textSecondary,
                  }}>
                    <Lock size={13} aria-hidden />
                    {t('visit.plan.locked')}
                  </span>
                )}
                <Badge variant="outline">
                  {labelFor(t, 'visit.plan.planStatus', plan.status)}
                </Badge>
              </div>
            )}
          >
            {items.length === 0 ? (
              <div style={{ fontSize: 13, color: C.textMuted }}>
                {t('patient.plan.noLines')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((item) => {
                  const tone = STATUS_TONE[item.status] || STATUS_TONE.proposed;
                  // In THIS plan's currency. A tariff in another currency — the
                  // catalogue is one clinic's, the plans may be several — is
                  // said in words, never shown as this plan's figure.
                  const price = linePrice(item, catalog, { locked, currency });
                  const sessions = sessionsOf(item, records);
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        border: `1px solid ${C.border}`, borderRadius: 10,
                        padding: '9px 11px', background: CT.surface,
                      }}
                    >
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: C.textSecondary,
                        minWidth: 38, textAlign: 'center', flexShrink: 0,
                      }}>
                        {item.toothFdi
                          ? <span {...LTR_TEXT}>{formatNumber(item.toothFdi)}</span>
                          : t('visit.plan.wholeMouthShort')}
                      </span>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 700, color: C.textPrimary,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {item.description || t('visit.plan.untitledItem')}
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>
                          {labelFor(t, 'visit.plan.phases', item.phase)}
                          {item.surfaces ? ` · ${item.surfaces}` : ''}
                          {sessions.multi ? ` · ${t('visit.plan.sessionsOf', {
                            done: formatNumber(sessions.done),
                            total: formatNumber(sessions.quoted),
                          })}` : ''}
                        </div>
                      </div>

                      <span
                        {...LTR_TEXT}
                        data-testid={`patient-plan-amount-${item.id}`}
                        style={{
                          fontSize: 12, fontWeight: price.amount ? 700 : 400, flexShrink: 0,
                          color: price.amount ? C.textPrimary : CT.warnInk,
                        }}
                      >
                        {price.amount
                          ? money(price.amount, price.currency)
                          : price.mismatch
                            ? t('visit.plan.otherCurrency', { currency: price.entryCurrency })
                            : t('visit.plan.noTariff')}
                      </span>

                      <span style={{
                        background: tone.bg, color: tone.ink, borderRadius: 8,
                        padding: '3px 9px', fontSize: 11.5, fontWeight: 700, flexShrink: 0,
                      }}>
                        {labelFor(t, 'visit.plan.status', item.status)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        );
      })}
    </div>
  );
};

export default PlanTab;
